import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { registerNativePush } from "@/lib/nativePush";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Access token may have expired — try a silent refresh, then re-check.
      try {
        const { data: r } = await api.post("/auth/refresh");
        if (r?.token) localStorage.setItem("eventom_token", r.token);
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e2) {
        // Keep the session on transient/network failures; only clear it on a real 401.
        if (e2?.response?.status === 401 || e?.response?.status === 401) {
          localStorage.removeItem("eventom_token");
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // P1.1: If OTP verification is pending, DO NOT establish auth state on refresh.
    // Redirect to /signup so user completes OTP. No authenticated session bypass.
    if (typeof window !== "undefined" && localStorage.getItem("pending_verify_email")) {
      setLoading(false);
      if (window.location.pathname !== "/signup" && window.location.pathname !== "/organizer/signup") {
        window.location.replace("/signup");
      }
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("eventom_token", data.token);
    setUser(data.user);
    registerNativePush().catch(() => {});
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // P1.1: register no longer returns a token — session is established ONLY after OTP verify.
    if (data.token) { localStorage.setItem("eventom_token", data.token); setUser(data.user); }
    else {
      // Persist pending verify state so a page refresh keeps user on OTP screen (no bypass)
      localStorage.setItem("pending_verify_email", data.user.email);
      localStorage.setItem("pending_verify_name", data.user.name || "");
    }
    return data.user;
  };

  const verifyEmail = async (email, otp) => {
    const { data } = await api.post("/auth/verify-email", { email, otp });
    if (data.token) localStorage.setItem("eventom_token", data.token);
    setUser(data.user);
    localStorage.removeItem("pending_verify_email");
    localStorage.removeItem("pending_verify_name");
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("eventom_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, verifyEmail, logout, refresh, formatApiError }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
